import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Pencil, Check, X, Users, FileText, Calculator, Shield, Search, User, Download, Plus } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { JobContractManagement } from "@/components/JobContractManagement";
import { PayrollManagement } from "@/components/payroll/PayrollManagement";
import { EmployeeForm } from "@/components/forms/EmployeeForm";
import { PayslipDownloadDialog } from "@/components/PayslipDownloadDialog";
import { LohnausweisExportDialog } from "@/components/LohnausweisExportDialog";

interface User {
  id: string;
  email: string;
  created_at: string;
  full_name: string | null;
  role: string;
}

interface Employee {
  id: string;
  name: string;
  email: string;
  role: string;
  firstname?: string;
  lastname?: string;
  status: string;
  created_at: string;
  monthly_salary?: number;
  hourly_rate?: number;
  work_type: string;
}

type UserRole = 'admin' | 'user' | 'department_admin';

export default function Admin() {
  const [users, setUsers] = useState<User[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState<"users" | "employees">("users");
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [editRate, setEditRate] = useState<string>("");
  const [userRates, setUserRates] = useState<Record<string, number>>({});
  const [editingRole, setEditingRole] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<UserRole>('user');
  const [activeTab, setActiveTab] = useState<"users" | "contracts" | "payroll">("users");
  const [editingEmployee, setEditingEmployee] = useState<string | null>(null);
  const [showEmployeeForm, setShowEmployeeForm] = useState(false);
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (!isAdmin) {
      toast({
        title: "Zugriff verweigert",
        description: "Sie haben keine Berechtigung für diese Seite",
        variant: "destructive",
      });
      navigate("/");
      return;
    }

    if (activeTab === "users") {
      fetchUsers();
      fetchEmployees();
    }
  }, [isAdmin]);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase.rpc('get_users_for_admin');
      
      if (error) {
        console.error('Error fetching users:', error);
        toast({
          title: "Fehler",
          description: "Fehler beim Laden der Benutzer",
          variant: "destructive",
        });
        return;
      }

      setUsers(data || []);

      // Fetch hourly rates from profiles table
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, hourly_rate');

      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
      } else {
        const ratesMap: Record<string, number> = {};
        profiles?.forEach(profile => {
          ratesMap[profile.id] = profile.hourly_rate || 50;
        });
        setUserRates(ratesMap);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const { data, error } = await supabase
        .from('employees')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching employees:', error);
        toast({
          title: "Fehler",
          description: "Fehler beim Laden der Mitarbeiter",
          variant: "destructive",
        });
        return;
      }

      setEmployees(data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const startEditing = (userId: string, currentRate: number | null) => {
    setEditingUser(userId);
    setEditRate(currentRate?.toString() || "50");
  };

  const cancelEditing = () => {
    setEditingUser(null);
    setEditRate("");
  };

  const startEditingRole = (userId: string, currentRole: string) => {
    setEditingRole(userId);
    setSelectedRole(currentRole as UserRole);
  };

  const cancelEditingRole = () => {
    setEditingRole(null);
    setSelectedRole('user');
  };

  const saveUserRole = async (userId: string) => {
    try {
      const { error } = await supabase.rpc('update_user_role_admin', {
        target_user_id: userId,
        new_role: selectedRole
      });

      if (error) {
        console.error('Error updating user role:', error);
        toast({
          title: "Fehler",
          description: error.message || "Fehler beim Aktualisieren der Rolle",
          variant: "destructive",
        });
        return;
      }

      // Update local state
      setUsers(prev => prev.map(user => 
        user.id === userId ? { ...user, role: selectedRole } : user
      ));

      setEditingRole(null);
      setSelectedRole('user');

      toast({
        title: "Erfolgreich",
        description: "Benutzerrolle wurde aktualisiert",
      });
    } catch (error) {
      console.error('Error updating user role:', error);
      toast({
        title: "Fehler",
        description: "Fehler beim Aktualisieren der Rolle",
        variant: "destructive",
      });
    }
  };

  const saveHourlyRate = async (userId: string) => {
    const rate = parseFloat(editRate);
    
    if (isNaN(rate) || rate <= 0) {
      toast({
        title: "Ungültiger Wert",
        description: "Bitte geben Sie einen gültigen Stundensatz ein",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ hourly_rate: rate })
        .eq('id', userId);

      if (error) {
        console.error('Error updating hourly rate:', error);
        toast({
          title: "Fehler",
          description: "Fehler beim Aktualisieren des Stundensatzes",
          variant: "destructive",
        });
        return;
      }

      // Update local state
      setUserRates(prev => ({
        ...prev,
        [userId]: rate
      }));

      setEditingUser(null);
      setEditRate("");

      toast({
        title: "Erfolgreich",
        description: "Stundensatz wurde aktualisiert",
      });
    } catch (error) {
      console.error('Error updating hourly rate:', error);
      toast({
        title: "Fehler",
        description: "Fehler beim Aktualisieren des Stundensatzes",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="container mx-auto p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="container mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-4">Admin Dashboard</h1>
          <div className="flex space-x-1 border-b">
            <button
              onClick={() => setActiveTab("users")}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg ${
                activeTab === "users"
              ? "bg-primary/10 text-primary border-b-2 border-primary"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <Users className="inline-block w-4 h-4 mr-2" />
              Benutzerverwaltung
            </button>
            <button
              onClick={() => setActiveTab("contracts")}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg ${
                activeTab === "contracts"
                  ? "bg-primary/10 text-primary border-b-2 border-primary"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <FileText className="inline-block w-4 h-4 mr-2" />
              Arbeitsverträge
            </button>
            <button
              onClick={() => setActiveTab("payroll")}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg ${
                activeTab === "payroll"
                  ? "bg-primary/10 text-primary border-b-2 border-primary"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <Calculator className="inline-block w-4 h-4 mr-2" />
              Lohnabrechnung
            </button>
          </div>
        </div>

        {activeTab === "users" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Benutzerverwaltung</span>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Search className="h-4 w-4" />
                    <Input
                      placeholder="Suchen..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-64"
                    />
                  </div>
                  <Select value={viewMode} onValueChange={(value: "users" | "employees") => setViewMode(value)}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="users">Benutzer</SelectItem>
                      <SelectItem value="employees">Mitarbeiter</SelectItem>
                    </SelectContent>
                  </Select>
                  {viewMode === "employees" && (
                    <Dialog open={showEmployeeForm} onOpenChange={setShowEmployeeForm}>
                      <DialogTrigger asChild>
                        <Button size="sm">
                          <Plus className="h-4 w-4 mr-2" />
                          Mitarbeiter hinzufügen
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>Neuer Mitarbeiter</DialogTitle>
                        </DialogHeader>
                        <EmployeeForm
                          onSuccess={() => {
                            setShowEmployeeForm(false);
                            fetchEmployees();
                          }}
                          onCancel={() => setShowEmployeeForm(false)}
                        />
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {viewMode === "users" ? (
                <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>E-Mail</TableHead>
                    <TableHead>Rolle</TableHead>
                    <TableHead>Registriert am</TableHead>
                    <TableHead>Stundensatz (CHF)</TableHead>
                    <TableHead>Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.filter(user => 
                    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    (user.full_name && user.full_name.toLowerCase().includes(searchTerm.toLowerCase()))
                  ).map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        {user.full_name || "Kein Name"}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        {editingRole === user.id ? (
                          <div className="flex items-center space-x-2">
                            <Select value={selectedRole} onValueChange={(value: UserRole) => setSelectedRole(value)}>
                              <SelectTrigger className="w-32">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                               <SelectItem value="user">Benutzer</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                                <SelectItem value="department_admin">Department Admin</SelectItem>
                              </SelectContent>
                            </Select>
                            <Button
                              size="sm"
                              onClick={() => saveUserRole(user.id)}
                              className="h-8 w-8 p-0"
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={cancelEditingRole}
                              className="h-8 w-8 p-0"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                           <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              user.role === 'admin' 
                                ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' 
                                : user.role === 'department_admin'
                                ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300'
                                : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
                            }`}>
                              {user.role === 'admin' ? 'Admin' : user.role === 'department_admin' ? 'Dept. Admin' : 'Benutzer'}
                            </span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => startEditingRole(user.id, user.role)}
                              className="h-8 w-8 p-0"
                            >
                              <Shield className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(user.created_at).toLocaleDateString('de-CH')}
                      </TableCell>
                      <TableCell>
                        {editingUser === user.id ? (
                          <div className="flex items-center space-x-2">
                            <Input
                              type="number"
                              value={editRate}
                              onChange={(e) => setEditRate(e.target.value)}
                              className="w-24"
                              min="0"
                              step="0.01"
                            />
                            <Button
                              size="sm"
                              onClick={() => saveHourlyRate(user.id)}
                              className="h-8 w-8 p-0"
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={cancelEditing}
                              className="h-8 w-8 p-0"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <span>{userRates[user.id] || 50} CHF</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {editingUser !== user.id && editingRole !== user.id && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => startEditing(user.id, userRates[user.id] || 50)}
                            className="h-8 w-8 p-0"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>E-Mail</TableHead>
                      <TableHead>Rolle</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Lohn</TableHead>
                      <TableHead>Aktionen</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {employees.filter(employee => 
                      employee.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      employee.name.toLowerCase().includes(searchTerm.toLowerCase())
                    ).map((employee) => (
                      <TableRow key={employee.id}>
                        <TableCell>{employee.name}</TableCell>
                        <TableCell>{employee.email}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            employee.role === 'admin' 
                              ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300' 
                              : employee.role === 'department_admin'
                              ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300'
                              : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
                          }`}>
                            {employee.role === 'admin' ? 'Admin' : employee.role === 'department_admin' ? 'Dept. Admin' : 'Mitarbeiter'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            employee.status === 'active' 
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' 
                              : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                          }`}>
                            {employee.status === 'active' ? 'Aktiv' : 'Inaktiv'}
                          </span>
                        </TableCell>
                        <TableCell>
                          {employee.work_type === 'hourly' 
                            ? `${employee.hourly_rate || 0} CHF/h`
                            : `${employee.monthly_salary || 0} CHF/m`
                          }
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button size="sm" variant="ghost">
                                  <User className="h-4 w-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                                <DialogHeader>
                                  <DialogTitle>Mitarbeiter bearbeiten: {employee.name}</DialogTitle>
                                </DialogHeader>
                                <EmployeeForm
                                  employeeId={employee.id}
                                  onSuccess={() => {
                                    fetchEmployees();
                                  }}
                                />
                              </DialogContent>
                            </Dialog>
                            <PayslipDownloadDialog 
                              employeeId={employee.id} 
                              employeeName={employee.name}
                            >
                              <Button size="sm" variant="ghost">
                                <Download className="h-4 w-4" />
                              </Button>
                            </PayslipDownloadDialog>
                            <LohnausweisExportDialog
                              preselectedEmployeeId={employee.id}
                              preselectedEmployeeName={employee.name}
                            >
                              <Button size="sm" variant="ghost" title="Lohnausweis exportieren">
                                <FileText className="h-4 w-4" />
                              </Button>
                            </LohnausweisExportDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === "contracts" && <JobContractManagement />}
        
        {activeTab === "payroll" && <PayrollManagement />}
      </div>
    </div>
  );
}
